import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { lighten, Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import IRider from '../../models/IRider';
import { TextField, Backdrop, CircularProgress, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { genericSort, getRiderComparator } from '../../services/SortingHelper';
import theme from '../../theme';
import { IGroup } from '../../models/IEvent';
import QuickEditRiderDialog, { IQuickEditRider } from './QuickEditRiderDialog';
import RiderServiceInstance from '../../services/RiderService';
import HelpTooltip from '../common/HelpTooltip';

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
interface HeadCell {
    disablePadding: boolean;
    id: keyof IRider;
    label: string;
    numeric: boolean;
    priority: number;
    maxChars?: number;
    tooltipKey?: string;
}

const headCells: HeadCell[] = [
    { id: 'position', numeric: true, disablePadding: true, label: 'rider.position', priority: 3, maxChars: 3, tooltipKey: 'tooltips.columnPosition' },
    { id: 'number', numeric: true, disablePadding: true, label: 'rider.number', priority: 3, maxChars: 7, tooltipKey: 'tooltips.columnNumber' },
    { id: 'group', numeric: false, disablePadding: true, label: 'rider.group', priority: 3, tooltipKey: 'tooltips.columnGroup' },
    { id: 'name', numeric: false, disablePadding: true, label: 'rider.name', priority: 1 },
    { id: 'vehicleModel', numeric: false, disablePadding: true, label: 'rider.vehicleModel', priority: 4 },
    { id: 'bestTime', numeric: true, disablePadding: true, label: 'rider.bestTime', priority: 1, tooltipKey: 'tooltips.columnBestTime' },
    { id: 'lastTime', numeric: true, disablePadding: true, label: 'rider.lastTime', priority: 2, tooltipKey: 'tooltips.columnLastTime' },
    { id: 'lastSeen', numeric: true, disablePadding: true, label: 'rider.seen', priority: 4, tooltipKey: 'tooltips.columnSeen' },
    { id: 'gapPrevious', numeric: true, disablePadding: true, label: 'rider.gapPrevious', priority: 4, tooltipKey: 'tooltips.columnGapPrevious' },
    { id: 'gapCircuitBest', numeric: true, disablePadding: true, label: 'rider.gapBestOfCircuit', priority: 4, tooltipKey: 'tooltips.columnGapCircuit' },
    { id: 'gapBest', numeric: true, disablePadding: true, label: 'rider.gapBestOfDay', priority: 4, tooltipKey: 'tooltips.columnGapBestOfDay' },
    { id: 'gapBestGroup', numeric: true, disablePadding: true, label: 'rider.gapBestOfGroup', priority: 4, tooltipKey: 'tooltips.columnGapBestOfGroup' },
    { id: 'lapCount', numeric: true, disablePadding: true, label: 'rider.lapCount', priority: 2, maxChars: 4, tooltipKey: 'tooltips.columnLaps' }
];

type Order = 'asc' | 'desc';

interface IHeadTableCellProps {
    headCell: HeadCell;
    classes: ReturnType<typeof useStyles>;
    order: Order;
    orderBy: string;
    createSortHandler: (property: keyof IRider) => (event: React.MouseEvent<unknown>) => void;
}

function getWidth(headCell: HeadCell): string | undefined {
    return headCell.maxChars !== undefined
        ? `${headCell.maxChars}ch`
        : undefined
}

const priorityBreakpointMap: Record<number, string> = {
    2: theme.breakpoints.up('sm'),
    3: theme.breakpoints.up('md'),
    4: theme.breakpoints.up('lg'),
};

function getSx2(headCell: HeadCell) {
    const width = getWidth(headCell);
    if (headCell.priority === 1) {
        return { width, display: 'table-cell' };
    }
    const bp = priorityBreakpointMap[headCell.priority] ?? theme.breakpoints.up('lg');
    return { width, display: 'none', [bp]: { display: 'table-cell' } };
}

function HeadTableCell(props: IHeadTableCellProps) {
    const { classes, order, orderBy, createSortHandler, headCell } = props;
    const { t } = useTranslation("common");
    const align = headCell.numeric ? 'right' : 'left';
    return (
        <TableCell
            key={headCell.id}
            align={align}
            padding={headCell.disablePadding ? 'none' : undefined}
            sortDirection={orderBy === headCell.id ? order : false}
            // sx = {{display: { sm: 'none', md: 'table-cell' }}} // Hidden on sm, visible on md and larger
            sx={getSx2(headCell)}
        >
            <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={align === 'right' ? { flexDirection: 'row-reverse' } : undefined}
            >
                {t(headCell.label)}
                {headCell.tooltipKey && <HelpTooltip text={t(headCell.tooltipKey)} />}
                {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                ) : null}
            </TableSortLabel>
        </TableCell>);
}

function ResponsiveHeadTableCell(props: IHeadTableCellProps) {
    const { classes, order, orderBy, createSortHandler, headCell } = props;
    return (<HeadTableCell headCell={headCell} orderBy={orderBy} order={order} classes={classes} createSortHandler={createSortHandler}></HeadTableCell>);
}

interface EnhancedTableProps {
    classes: ReturnType<typeof useStyles>;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IRider) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
    isOrganizer?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { classes, order, orderBy, onRequestSort, isOrganizer } = props;
    const { t } = useTranslation("common");
    const createSortHandler = (property: keyof IRider) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) =>
                    (<ResponsiveHeadTableCell key={headCell.id} headCell={headCell} orderBy={orderBy} order={order} classes={classes} createSortHandler={createSortHandler}></ResponsiveHeadTableCell>)
                )}
                {isOrganizer && (
                    <TableCell align="center" padding="none" sx={{ width: '50px', display: 'none', [theme.breakpoints.up('sm')]: { display: 'table-cell' } }}>
                        {t('rider.achieved')}<HelpTooltip text={t('tooltips.columnAchieved')} />
                    </TableCell>
                )}
                {isOrganizer && (
                    <TableCell align="center" padding="none" sx={{ width: '50px' }}>
                    </TableCell>
                )}
            </TableRow>
        </TableHead >
    );
}

const useToolbarStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1),
        },
        highlight:
            theme.palette.mode === 'light'
                ? {
                    color: theme.palette.secondary.main,
                    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
                }
                : {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.secondary.dark,
                },
        title: {
            flex: '1 1 100%',
        },
    }),
);

interface EnhancedTableToolbarProps {
    // numSelected: number;
    riderFilterChanged: any;
    groups?: IGroup[];
    selectedGroups: string[];
    onGroupFilterChanged: (groupIds: string[]) => void;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    const classes = useToolbarStyles();
    const { t } = useTranslation("common");
    // const { numSelected } = props;

    const hasGroups = props.groups && props.groups.length > 0;

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: false,
            })}
            sx={{ flexWrap: 'wrap', height: 'auto', py: 1, gap: 1 }}
        >
            <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                {t("rider.list")}
            </Typography>
            <TextField label={t("rider.filter")} id="riderfilter" onChange={props.riderFilterChanged} size="small" />
            {hasGroups && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="group-filter-label">{t("rider.filterByGroup")}</InputLabel>
                    <Select
                        labelId="group-filter-label"
                        id="group-filter"
                        multiple
                        value={props.selectedGroups}
                        onChange={(e) => {
                            const value = e.target.value;
                            props.onGroupFilterChanged(typeof value === 'string' ? value.split(',') : value);
                        }}
                        input={<OutlinedInput label={t("rider.filterByGroup")} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((groupId) => {
                                    const group = props.groups?.find(g => g.groupId === groupId);
                                    return <Chip key={groupId} label={group?.name ?? groupId} size="small" />;
                                })}
                            </Box>
                        )}
                    >
                        {props.groups?.map((g) => (
                            <MenuItem
                                key={g.groupId}
                                value={g.groupId}
                                sx={props.selectedGroups.includes(g.groupId)
                                    ? { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'primary.contrastText', '&:hover': { backgroundColor: 'primary.dark' } }
                                    : undefined}
                            >
                                {g.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            <Tooltip title="Filter list">
                <IconButton aria-label="filter list" size="large">
                    <FilterListIcon />
                </IconButton>
            </Tooltip>

        </Toolbar>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 280,
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
    }),
);

interface IEventProps {
    eventId: string;
    riders: IRider[];
    isOrganizer?: boolean;
    groups?: IGroup[];
    onRiderUpdate?: (tagIdFriendly: string, trial: boolean, group: string) => void;
    blinkingRiders?: Set<string>;
    selectedRound?: string;
}
function RidersTable(props: IEventProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof IRider>('bestTime');
    const [selected, setSelected] = React.useState<string[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(-1);
    const [riders, setRiders] = React.useState(props.riders);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRider, setEditingRider] = useState<IQuickEditRider | null>(null);
    const [saving, setSaving] = useState(false);
    const [nameFilter, setNameFilter] = useState('');
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

    useEffect(() => {
        let filtered = props.riders;
        if (nameFilter.length > 0) {
            filtered = filtered.filter((r) => r.name.toLowerCase().includes(nameFilter));
        }
        if (selectedGroups.length > 0) {
            filtered = filtered.filter((r) => selectedGroups.includes(r.groupId));
        }
        setRiders(filtered);
    }, [props.riders, nameFilter, selectedGroups]);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IRider) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = riders.map((r) => r.tagIdFriendly);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<unknown>, tagId: string) => {
        const path = props.selectedRound
            ? `/rider/${props.eventId}/${tagId}?round=${props.selectedRound}`
            : `/rider/${props.eventId}/${tagId}`;
        navigate(path);
    };

    const handleEditClick = (event: React.MouseEvent<unknown>, rider: IRider) => {
        event.stopPropagation();
        setEditingRider({
            tagIdFriendly: rider.tagIdFriendly,
            name: rider.name,
            trial: rider.trial,
            group: rider.groupId
        });
        setEditDialogOpen(true);
    };

    const handleEditSave = (tagIdFriendly: string, trial: boolean, group: string) => {
        if (props.onRiderUpdate) {
            props.onRiderUpdate(tagIdFriendly, trial, group);
        }
        // Update the rider in the server side
        setSaving(true);
        setEditDialogOpen(false);
        RiderServiceInstance.reorganizeRider(props.eventId, tagIdFriendly, !trial, group)
            .then((updatedRider) => {
                setRiders(prevRiders => prevRiders.map(r =>
                    r.tagIdFriendly === updatedRider.tagIdFriendly
                        ? updatedRider
                        : r
                ));
            }).catch((error) => {
                console.error("Failed to update rider:", error);
            }).finally(() => {
                setSaving(false);
                setEditingRider(null);
            });
    };

    const handleEditCancel = () => {
        setEditDialogOpen(false);
        setEditingRider(null);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (tagId: string) => selected.indexOf(tagId) !== -1;

    const emptyRows = rowsPerPage > 0 ? rowsPerPage - Math.min(rowsPerPage, riders.length - page * rowsPerPage) : 0;

    const riderFilterChanged = (event: any) => {
        setNameFilter(event.target.value.toLowerCase());
    };

    const groupFilterChanged = (groupIds: string[]) => {
        setSelectedGroups(groupIds);
    };

    const pageOffset = rowsPerPage > 0 ? page * rowsPerPage : 0;

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <EnhancedTableToolbar
                    riderFilterChanged={riderFilterChanged}
                    groups={props.groups}
                    selectedGroups={selectedGroups}
                    onGroupFilterChanged={groupFilterChanged}
                />
                <TableContainer component={Paper}>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={'small'}
                        aria-label="enhanced table"
                        stickyHeader
                        sx={{
                            '& td': { borderBottom: '1px solid', borderColor: 'grey.300', px: '6px', py: '4px' },
                            '& th': { borderBottom: '1px solid', borderColor: 'grey.600', px: '6px', py: '4px' },
                        }}
                    >
                        <EnhancedTableHead
                            classes={classes}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={riders.length}
                            isOrganizer={props.isOrganizer}
                        />
                        <TableBody>
                            {genericSort(riders, getRiderComparator(order, orderBy))
                                .slice(pageOffset, rowsPerPage > 0 ? pageOffset + rowsPerPage : undefined)
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.tagIdFriendly.toString());
                                    const isBlinking = props.blinkingRiders?.has(row.tagIdFriendly) ?? false;

                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) => handleClick(event, row.tagIdFriendly.toString())}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.tagIdFriendly}
                                            selected={isItemSelected}
                                            sx={isBlinking ? { '&&&': { backgroundColor: 'warning.main', '& .MuiTableCell-root': { fontWeight: 'bold' } } } : undefined}
                                        >
                                            {headCells.map((headCell) => (
                                                <TableCell
                                                    key={headCell.id}
                                                    align={headCell.numeric ? 'right' : 'left'}
                                                    padding={headCell.disablePadding ? 'none' : undefined}
                                                    sx={getSx2(headCell)} // Apply responsive styles
                                                >
                                                    {headCell.id === 'position' ? pageOffset + index + 1 : row[headCell.id]}
                                                </TableCell>
                                            ))}
                                            {props.isOrganizer && (
                                                <TableCell align="center" padding="none" sx={{ display: 'none', [theme.breakpoints.up('sm')]: { display: 'table-cell' } }}>
                                                    {row.achieved ? (
                                                        <Tooltip title={t('rider.achieved')}>
                                                            <ThumbUpIcon fontSize="small" sx={{ color: 'success.main' }} aria-label={t('rider.achieved')} />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={t('rider.notAchieved')}>
                                                            <ThumbDownIcon fontSize="small" sx={{ color: 'error.main' }} aria-label={t('rider.notAchieved')} />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                            {props.isOrganizer && (
                                                <TableCell align="center" padding="none">
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(event) => handleEditClick(event, row)}
                                                            aria-label="edit"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 33 * emptyRows }}>
                                    <TableCell colSpan={props.isOrganizer ? 14 : 12} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, { value: -1, label: 'All' }]}
                    component="div"
                    count={riders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    onPageChange={handleChangePage}
                />
            </Paper>
            {props.isOrganizer && (
                <QuickEditRiderDialog
                    open={editDialogOpen}
                    rider={editingRider}
                    groups={props.groups ?? []}
                    onSave={handleEditSave}
                    onCancel={handleEditCancel}
                />
            )}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={saving}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {/* <FormControlLabel
                control={<Switch checked={dense} onChange={handleChangeDense} />}
                label="Dense padding"
            /> */}
        </div>
    );
}
export default RidersTable;